import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Camera, CameraOff, Mic, MicOff, Wifi, WifiOff, CheckCircle2, AlertTriangle, Loader2, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useLiveVideo } from '../context/LiveVideoContext';
import { toast } from 'sonner';

const NETWORK_PING_SAMPLES = 5;
const GOOD_LATENCY_MS = 150;
const OK_LATENCY_MS = 350;

function StatusPill({ state, labels }) {
  const map = {
    pending: { cls: 'bg-muted-foreground/20 text-muted-foreground', label: labels.pending },
    ok: { cls: 'bg-green-500/20 text-green-400', label: labels.ok },
    warn: { cls: 'bg-yellow-500/20 text-yellow-400', label: labels.warn || labels.ok },
    fail: { cls: 'bg-red-500/20 text-red-400', label: labels.fail },
  };
  const m = map[state] || map.pending;
  return <span className={`text-xs px-2 py-1 rounded font-medium ${m.cls}`}>{m.label}</span>;
}

export default function LiveWorkoutPreflightPage() {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { user, api, refreshUser } = useAuth();
  const live = useLiveVideo();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);

  const [camState, setCamState] = useState('pending'); // pending|ok|fail
  const [micState, setMicState] = useState('pending');
  const [netState, setNetState] = useState('pending'); // pending|ok|warn|fail
  const [micLevel, setMicLevel] = useState(0); // 0-100
  const [latency, setLatency] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [requesting, setRequesting] = useState(true);
  const [joining, setJoining] = useState(false);

  // Refresh user on mount so Premium status reflects any recent DB changes
  useEffect(() => {
    refreshUser?.();
  }, [refreshUser]);

  // Request camera + mic
  useEffect(() => {
    // Skip media requests for non-premium users (they'll be sent to upgrade)
    if (!user?.is_admin && !user?.is_premium) return undefined;
    let mounted = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: true,
        });
        if (!mounted) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        // Camera ok if we got at least one video track
        setCamState(stream.getVideoTracks().length > 0 ? 'ok' : 'fail');
        // Mic ok if at least one audio track
        setMicState(stream.getAudioTracks().length > 0 ? 'ok' : 'fail');

        // Wire up audio level meter
        if (stream.getAudioTracks().length > 0) {
          const AC = window.AudioContext || window.webkitAudioContext;
          const ctx = new AC();
          audioCtxRef.current = ctx;
          const src = ctx.createMediaStreamSource(stream);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 256;
          src.connect(analyser);
          const data = new Uint8Array(analyser.frequencyBinCount);
          const tick = () => {
            analyser.getByteFrequencyData(data);
            const avg = data.reduce((s, v) => s + v, 0) / data.length;
            setMicLevel(Math.min(100, Math.round((avg / 128) * 100)));
            rafRef.current = requestAnimationFrame(tick);
          };
          tick();
        }
      } catch (err) {
        console.error('preflight permission error:', err);
        const denied = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError';
        const noDev = err?.name === 'NotFoundError';
        setCamState('fail');
        setMicState('fail');
        if (denied) setErrorMsg('Camera/mic permission was denied. Allow access in your browser settings and reload.');
        else if (noDev) setErrorMsg('No camera or microphone found on this device.');
        else setErrorMsg(err?.message || 'Unable to access camera/mic.');
      } finally {
        if (mounted) setRequesting(false);
      }
    })();
    return () => {
      mounted = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  // Network ping test (median of N samples to /api/auth/me)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const samples = [];
      for (let i = 0; i < NETWORK_PING_SAMPLES; i++) {
        if (cancelled) return;
        const t0 = performance.now();
        try {
          await api().get('/auth/me');
          samples.push(performance.now() - t0);
        } catch {
          samples.push(2000);
        }
        await new Promise(r => setTimeout(r, 120));
      }
      samples.sort((a, b) => a - b);
      const median = Math.round(samples[Math.floor(samples.length / 2)]);
      if (cancelled) return;
      setLatency(median);
      setNetState(median <= GOOD_LATENCY_MS ? 'ok' : median <= OK_LATENCY_MS ? 'warn' : 'fail');
    })();
    return () => { cancelled = true; };
  }, [api]);

  const allChecksPassed = camState === 'ok' && micState === 'ok' && (netState === 'ok' || netState === 'warn');

  const handleEnter = async () => {
    if (joining) return;
    setJoining(true);
    // Stop the local preview stream — LiveKit will request fresh tracks
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());

    // Fetch session details to know if a workout is linked
    let workoutId = null;
    try {
      const res = await api().get(`/buddies/workout-invite/${sessionId}`);
      if (res.data?.session?.workout_id && res.data.session.workout_id !== 'live') {
        workoutId = res.data.session.workout_id;
      }
    } catch { /* falls back to freestyle landing */ }

    // Start the global LiveKit session — floating PiP appears app-wide
    const result = await live.startSession(sessionId);
    if (!result.ok) {
      setJoining(false);
      return;
    }

    // Workout-linked → land on the workout page (where the real timer + sync lives).
    // Freestyle → fall back to the simpler in-room view.
    if (workoutId) {
      navigate(`/workouts/${workoutId}`);
    } else {
      navigate(`/live-workout/${sessionId}/room`);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="live-preflight-page">
      <header className="border-b border-border px-6 py-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-white" data-testid="preflight-back-btn" aria-label="back">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="font-heading text-xl uppercase tracking-wider">Pre-flight Check</h1>
        <button
          onClick={() => navigate(`/live-workout/${sessionId}/recordings`)}
          className="ml-auto text-xs text-muted-foreground hover:text-white underline-offset-4 hover:underline"
          data-testid="preflight-view-replays-btn"
        >
          View Replays
        </button>
      </header>

      {/* Premium gate (shown inline so non-premium users don't waste time on hardware checks) */}
      {!user?.is_admin && !user?.is_premium ? (
        <main className="max-w-md mx-auto px-6 py-12 text-center" data-testid="preflight-premium-gate">
          <Crown className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
          <h2 className="font-heading text-2xl uppercase tracking-wider mb-2">Premium Feature</h2>
          <p className="text-muted-foreground mb-6">
            Live video sessions are a Premium feature. Upgrade to invite your buddy live!
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate(-1)} className="flex-1">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <Button onClick={() => navigate('/subscription')} className="flex-1 bg-yellow-500 text-black hover:bg-yellow-600">
              <Crown className="h-4 w-4 mr-2" /> Upgrade
            </Button>
          </div>
        </main>
      ) : (
      <main className="max-w-3xl mx-auto px-6 py-8">
        <p className="text-muted-foreground text-sm mb-6">
          Make sure your camera, mic, and connection are good before entering the live room with your buddy.
        </p>

        {/* Camera preview */}
        <div className="bg-card border border-border p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {camState === 'ok' ? <Camera className="h-5 w-5 text-green-400" /> : <CameraOff className="h-5 w-5 text-muted-foreground" />}
              <span className="font-heading uppercase tracking-wider text-sm">Camera</span>
            </div>
            <StatusPill state={camState} labels={{ pending: 'Checking…', ok: 'Working', fail: 'Blocked' }} />
          </div>
          <div className="relative aspect-video bg-black/60 overflow-hidden rounded">
            {requesting && (
              <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Requesting camera permission…
              </div>
            )}
            {!requesting && camState === 'fail' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-muted-foreground p-6">
                <AlertTriangle className="h-8 w-8 text-red-400 mb-2" />
                <p className="text-sm">Camera unavailable</p>
              </div>
            )}
            {/* Local camera preview — no audio output, no captions needed */}
            <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="preflight-camera-preview" />
          </div>
        </div>

        {/* Mic level meter */}
        <div className="bg-card border border-border p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {micState === 'ok' ? <Mic className="h-5 w-5 text-green-400" /> : <MicOff className="h-5 w-5 text-muted-foreground" />}
              <span className="font-heading uppercase tracking-wider text-sm">Microphone</span>
            </div>
            <StatusPill state={micState} labels={{ pending: 'Checking…', ok: 'Working', fail: 'Blocked' }} />
          </div>
          <p className="text-xs text-muted-foreground mb-2">Say something — the bar should jump when you speak.</p>
          <div className="h-3 w-full bg-black/60 rounded overflow-hidden" data-testid="preflight-mic-level">
            <div
              className="h-full bg-gradient-to-r from-green-500 via-yellow-400 to-red-500 transition-all duration-75"
              style={{ width: `${micLevel}%` }}
            />
          </div>
        </div>

        {/* Network test */}
        <div className="bg-card border border-border p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {netState === 'fail' ? <WifiOff className="h-5 w-5 text-red-400" /> : <Wifi className={`h-5 w-5 ${netState === 'ok' ? 'text-green-400' : netState === 'warn' ? 'text-yellow-400' : 'text-muted-foreground'}`} />}
              <span className="font-heading uppercase tracking-wider text-sm">Connection</span>
            </div>
            <StatusPill
              state={netState}
              labels={{ pending: 'Testing…', ok: 'Excellent', warn: 'OK', fail: 'Poor' }}
            />
          </div>
          {latency !== null && (
            <p className="text-xs text-muted-foreground">
              Median latency: {latency}ms{netState === 'warn' && ' — video may be a bit choppy.'}{netState === 'fail' && ' — your connection may struggle with live video.'}
            </p>
          )}
        </div>

        {errorMsg && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-300 p-4 mb-4 rounded text-sm" data-testid="preflight-error">
            {errorMsg}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleEnter}
            disabled={!allChecksPassed || joining}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white font-heading uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="preflight-enter-btn"
          >
            {joining ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            {joining ? 'Joining...' : allChecksPassed ? 'Enter Live Room' : 'Resolve issues to continue'}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              if (window.confirm('Skip checks and join the room anyway? Your buddy may not see/hear you.')) {
                handleEnter();
              }
            }}
            className="sm:w-48"
            data-testid="preflight-skip-btn"
          >
            Skip checks
          </Button>
        </div>

        {!allChecksPassed && !requesting && (
          <p className="text-xs text-muted-foreground mt-4 text-center">
            {camState === 'fail' || micState === 'fail'
              ? 'Tip: click the camera icon in the address bar to grant permissions, then reload this page.'
              : 'Connection looks weak. Try moving closer to your router or switching to Wi-Fi.'}
          </p>
        )}
      </main>
      )}
    </div>
  );
}
